import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Rating from "@mui/material/Rating";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { Link as RouterLink } from "react-router-dom";
import { getImageUrl } from "../../../services/indexMediaBaseUrl";
import { useTranslation } from "react-i18next";

export default function ProductDialog({ product = null, open = false, onClose = () => {} }) {
  const { t, i18n } = useTranslation("product_dialog");
  if (!product) return null;

  // new: helpers to pick price based on stored country_id
  const getSelectedCountryId = () => {
    try {
      const v = localStorage.getItem("country_id");
      return v !== null ? String(v) : null;
    } catch {
      return null;
    }
  };

  const getPriceInfo = (prod) => {
    if (!prod) return null;
    const countryId = getSelectedCountryId();
    const currencies = prod.currencies || prod.Currencies || [];
    let entry = null;
    if (countryId) {
      entry = currencies.find((c) => String(c.country_id) === String(countryId));
    }
    if (!entry && currencies.length) entry = currencies[0];
    if (!entry) {
      if (prod.price || prod.price_after_discount) {
        return {
          price: prod.price ? String(prod.price) : null,
          price_after_discount: parseFloat(prod.price_after_discount) > 0 ? String(prod.price_after_discount) : null,
        };
      }
      return null;
    }
    return {
      price: entry.price ? String(entry.price) : null,
      price_after_discount: parseFloat(entry.price_after_discount) > 0 ? String(entry.price_after_discount) : null,
    };
  };

  const formatPrice = (p) => (p == null ? "" : p);

  const priceInfo = getPriceInfo(product);

  // determine lang like BestSellers example (prefer i18n, fallback localStorage)
  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";
  const name = lang === "ar" ? (product.name_ar || product.name_en) : (product.name_en || product.name_ar);
  const desc = lang === "ar" ? (product.desc_ar || product.desc_en) : (product.desc_en || product.desc_ar);
  const imgSrc = getImageUrl(product.main_photo || (product.images && product.images[0] && product.images[0].image) || "");
  const ratings = product.reviews_summary || {};
  const reviews = Array.isArray(product.reviews) ? product.reviews.filter(r => r.approved) : [];
  const currencies = product.currencies || [];

  const formatDate = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pr: 6, fontWeight: 700 }}>
        <Box component={RouterLink} to={`/product/${product.id}`} sx={{ textDecoration: "none", color: "inherit" }}>
          {name}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Box sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 3, p: 2, bgcolor: "#fafafa" }}>
              <Box component={RouterLink} to={`/product/${product.id}`} sx={{ display: "block" }}>
                <img
                  src={imgSrc || "/placeholder.png"}
                  alt={name}
                  style={{ width: "100%", objectFit: "contain", maxHeight: 360, borderRadius: 8 }}
                />
              </Box>
              {product.images && product.images.length > 1 && (
                <Stack direction="row" spacing={1} sx={{ mt: 1, overflowX: "auto" }}>
                  {product.images.map((it, i) => (
                    <Avatar
                      key={i}
                      variant="rounded"
                      component={RouterLink}
                      to={`/product/${product.id}`}
                      src={getImageUrl(it.image || it.url) || undefined}
                      sx={{ width: 56, height: 56 }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">{desc}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={7}>
            <Stack spacing={1}>
              {/* category + metadata */}
              <Stack direction="row" spacing={1} alignItems="center">
                {product.categroy?.id && (
                  <Chip
                    component={RouterLink}
                    to={`/category/${product.categroy.id}`}
                    label={(i18n?.language || "ar").startsWith("ar") ? (product.categroy.name_ar || product.categroy.name_en) : (product.categroy.name_en || product.categroy.name_ar)}
                    clickable
                  />
                )}
                <Chip label={product.status ? t("status_active","نشط") : t("status_inactive","غير نشط")} />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {t("created_at","أنشئ")}: {formatDate(product.created_at)}
                </Typography>
              </Stack>

              {/* price block based on selected country */}
              {priceInfo && (
                <Box>
                  {priceInfo.price_after_discount ? (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ textDecoration: "line-through" }}>
                        {formatPrice(priceInfo.price)}
                      </Typography>
                      <Typography variant="h5" color="primary">
                        {formatPrice(priceInfo.price_after_discount)}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="h5" color="primary" sx={{ mt: 1 }}>
                      {formatPrice(priceInfo.price)}
                    </Typography>
                  )}
                </Box>
              )}

              <Divider />

              {/* summary */}
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="h6">
                  {ratings.average_rating ? `${ratings.average_rating} ★` : t("no_rating","بدون تقييم")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("total_reviews","مجموع المراجعات")}: {ratings.total_reviews || 0}
                </Typography>
              </Stack>

              {/* ratings breakdown */}
              {ratings.ratings_count && (
                <Box>
                  {Object.entries(ratings.ratings_count).sort((a,b)=>b[0]-a[0]).map(([star,count]) => (
                    <Box key={star} sx={{ display: "flex", alignItems: "center", gap: 1, my: 0.5 }}>
                      <Typography variant="body2" sx={{ width: 36 }}>{`${star}★`}</Typography>
                      <Box sx={{ flex: 1, bgcolor: "#f0f0f0", height: 8, borderRadius: 1, overflow: "hidden" }}>
                        <Box
                          sx={{
                            height: "100%",
                            bgcolor: "primary.main",
                            width: `${Math.min(100, ((count || 0) / (ratings.total_reviews || 1)) * 100)}%`
                          }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ width: 36, textAlign: "right" }}>{count}</Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* sizes */}
              {product.sizes && product.sizes.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1">{t('sizes', { defaultValue: 'المقاسات' })}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                    {product.sizes.map((s) => (
                      <Chip key={s.id || s.key} label={s.size || s.key} size="small" />
                    ))}
                  </Stack>
                </Box>
              )}

              {/* colors */}
              {product.colors && product.colors.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1">{t('colors', { defaultValue: 'الألوان' })}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", alignItems: "center" }}>
                    {product.colors.map((c) => (
                      <Chip
                        key={c.id}
                        avatar={c.code ? <Avatar sx={{ bgcolor: c.code }} /> : undefined}
                        label={lang === "ar" ? (c.color_ar || c.color_en) : (c.color_en || c.color_ar) || `Color ${c.id}`}
                        size="small"
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              <Divider />


              <Divider />

              {/* reviews list */}
              <Box>
                <Typography variant="subtitle1">{t("reviews", { defaultValue: "التعليقات" })}</Typography>
                {reviews.length ? (
                  <List>
                    {reviews.map((r) => (
                      <ListItem key={r.id} alignItems="flex-start">
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Rating value={Number(r.rating)} size="small" readOnly />
                              <Typography variant="body2" sx={{ fontWeight: "bold" }}>{r.user_id ? `User ${r.user_id}` : ""}</Typography>
                            </Box>
                          }
                          secondary={
                            <>
                              <Typography variant="body2" color="text.primary">{r.review}</Typography>
                              <Typography variant="caption" color="text.secondary">{formatDate(r.created_at)}</Typography>
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">{t("no_reviews", { defaultValue: "لا توجد تعليقات" })}</Typography>
                )}
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
